import type { VotingQuestion } from '../../api/DigiobcanApi';
import {
  answerMeta,
  isQuestionAnswered,
  type VotingAnswers,
} from '../../lib/votings';
import { __ } from '../../utils';
import { AnswerSummary, QuestionSummaryCard } from './QuestionSummary';
import { Box, Button, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  questions: VotingQuestion[];
  answers: VotingAnswers;
  onEdit: (index: number) => void;
};

const CheckMark: React.FC<{ ok: boolean }> = ({ ok }) => (
  <Box
    aria-hidden
    sx={({ palette, shape }) => ({
      flex: 'none',
      width: 22,
      height: 22,
      display: 'grid',
      placeItems: 'center',
      borderRadius: `${shape.borderRadiusFull}px`,
      backgroundColor: ok ? palette.green.main : palette.yellow.main,
      color: ok ? palette.common.white : palette.grey[900],
      fontSize: 13,
      fontWeight: 700,
    })}
  >
    {ok ? (
      <Box
        component='svg'
        viewBox='0 0 24 24'
        width={14}
        height={14}
        fill='none'
        stroke='currentColor'
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M5 13l4 4L19 7' />
      </Box>
    ) : (
      '!'
    )}
  </Box>
);

/**
 * Kontrola pred odoslaním - posledný krok sústredeného režimu.
 *
 * Pri tempe „jedna otázka na obrazovku" je to jediné miesto, kde občan vidí
 * celok; bez neho by odosielal lístok, ktorý si po ceste nemohol prečítať.
 * Neúplná otázka je žltá a jej akcia sa volá „Doplniť", nie „Zmeniť" - to je
 * celý rozdiel medzi „pozri sa na to" a „tu niečo chýba".
 *
 * **Odpoveď je štruktúra, nie prúd viet** ([[QuestionSummary]]). Kým sa
 * vypisovala riadok za riadkom z `answerLines`, mala otázka s dvadsiatimi
 * šiestimi tvrdeniami škály dvadsaťšesť viet v tvare „E0101 Názov -
 * nehodnotené" a kontrola z toho bola dlhšia než samotné hlasovanie. Ten
 * výpis zostáva pre odovzdaný lístok ([[VotingTicket]]), kde je zoznam
 * krátky a nič sa v ňom nemení.
 *
 * Meta riadok hovorí o odpovedi (`answerMeta`), nie o otázke: „ohodnotené 4
 * z 26 tvrdení" je to, čo má hlasujúci skontrolovať, kým „škála 0 - 3 · 26
 * tvrdení" je zadanie, ktoré už videl na kroku otázky.
 */
const VotingReview: React.FC<Props> = ({ questions, answers, onEdit }) => (
  <Stack gap={1.5}>
    <Stack gap={0.75} sx={{ px: 0.5 }}>
      <Typography
        component='h2'
        sx={{ fontSize: { xs: 21, md: 26 }, fontWeight: 700, lineHeight: 1.25 }}
      >
        {__('Skontrolujte odpovede')}
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        {__(
          'Po odoslaní sa už nedajú zmeniť. Kliknutím na otázku sa vrátite k nej.',
        )}
      </Typography>
    </Stack>

    {questions.map((question, index) => {
      const ok = isQuestionAnswered(question, answers[question.question_id]);

      return (
        <QuestionSummaryCard
          key={question.question_id}
          mark={<CheckMark ok={ok} />}
          meta={`${__('Otázka')} ${index + 1} · ${answerMeta(question, answers[question.question_id])}`}
          metaTone={ok ? 'neutral' : 'warning'}
          title={question.title}
          action={
            <Button
              variant='outline'
              size='small'
              onClick={() => onEdit(index)}
              sx={
                ok
                  ? undefined
                  : { borderColor: 'yellow.main', color: 'yellow.surfaceText' }
              }
            >
              {ok ? __('Zmeniť') : __('Doplniť')}
            </Button>
          }
        >
          <AnswerSummary
            question={question}
            answers={answers[question.question_id]}
          />
        </QuestionSummaryCard>
      );
    })}
  </Stack>
);

export default VotingReview;
